import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Grid } from '@mui/material';
import {
  People as PeopleIcon,
  Devices as DevicesIcon,
  CheckCircle,
  Key as KeyIcon,
  ErrorOutline,
  Block,
  Apps as AppsIcon,
} from '@mui/icons-material';
import KpiGrid from '../dashboard/shared/KpiGrid';
import AuthAnalyticsSection from '../dashboard/shared/AuthAnalyticsSection';
import DeviceAnalyticsSection from '../dashboard/shared/DeviceAnalyticsSection';
import ApplicationSelector from '../dashboard/business/ApplicationSelector';
import SubscriberAnalyticsSection from '../dashboard/business/SubscriberAnalyticsSection';
// import PasskeySection from '../dashboard/business/PasskeySection';
import { businessDashboardService } from '../../services/dashboard';
import { useAuth } from '../../context/AuthContext';

const overviewKpis = [
  { key: 'subscribers',
    title: 'Subscribers',
    icon: <PeopleIcon />,
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total unique subscribers on the business',
    subtitle: (data) => (data.subscribers.newInWindow ? `+${data.subscribers.newInWindow} this month` : undefined),
    trend: (data) => (data.subscribers.changePct >= 0 ? 'up' : 'down'),
    trendValue: (data) => `${Math.abs(data.subscribers.changePct)}%`,
  },
  { key: 'activeDevices', 
    title: 'Active Devices', 
    icon: <DevicesIcon />, 
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total number of subscriber devices that have been active',
  },
  { key: 'authRequests', 
    title: 'Auth Requests', 
    icon: <CheckCircle />, 
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total number of authentication transactions in the last 30 days.',
    subtitle: (data) => (data.authRequests.value ? `+${data.authRequests.value} this month` : undefined),
    trend: (data) => (data.authRequests.changePct >= 0 ? 'up' : 'down'),
    trendValue: (data) => `${Math.abs(data.authRequests.changePct)}%`,
  },
  { key: 'successRate',
    title: 'Success Rate',
    icon: <CheckCircle />,
    format: (v) => `${v.value}%`,
    tooltip: 'Success rate of authentication transactions in the last 30 days.',
  },
  { key: 'failureRate',
    title: 'Failure Rate',
    icon: <ErrorOutline />,
    format: (v) => `${v.value}%`,
    tooltip: 'Failure rate of authentication transactions in the last 30 days.',
  },
  // { key: 'passkeys', title: 'Passkeys', icon: <KeyIcon />, format: (v) => v.value.toLocaleString() },
  { key: 'failedAuth',
    title: 'Failed Auth',
    icon: <ErrorOutline />,
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total number of failed authentication transactions in the last 30 days.',
  },
  { key: 'blockedUsers', 
    title: 'Blocked Users', 
    icon: <Block />, 
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Total blocked subscribers on the business',
  },
  { key: 'activeApplications', 
    title: 'Active Apps', 
    icon: <AppsIcon />, 
    format: (v) => v.value.toLocaleString(),
    tooltip: 'Active applications of the business',
  },
];

const TABS = ['Authentication', 'Subscribers', 'Devices'];

const BusinessDashboard = () => {
  const { user } = useAuth();
  const [applicationId, setApplicationId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setOverviewLoading(true);
    setOverviewError(null);
    businessDashboardService
      .getOverview(applicationId, 30)
      .then((res) => setOverview(res.data))
      .catch((e) => setOverviewError(e.message))
      .finally(() => setOverviewLoading(false));
  }, [applicationId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Business Dashboard
      </Typography>

      <ApplicationSelector value={applicationId} onChange={setApplicationId} businessName={user?.businessName || user?.name} />

      <Box sx={{ mb: 3 }}>
        <KpiGrid items={overviewKpis} data={overview} loading={overviewLoading} error={overviewError} />
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {tab === 0 && (
            <AuthAnalyticsSection
              fetchVolume={(period) => businessDashboardService.getAuthenticationVolume(period, applicationId)}
              fetchMethods={(period) => businessDashboardService.getAuthenticationMethods(period, applicationId)}
              fetchStatus={(period) => businessDashboardService.getAuthenticationStatus(period, applicationId)}
              reloadKey={applicationId}
            />
          )}
          {tab === 1 && <SubscriberAnalyticsSection applicationId={applicationId} reloadKey={applicationId} />}
          {tab === 2 && (
            <DeviceAnalyticsSection
              fetchDevices={() => businessDashboardService.getDevices('30d', applicationId)}
              reloadKey={applicationId}
            />
          )}
          {/* {tab === 3 && <PasskeySection applicationId={applicationId} reloadKey={applicationId} />} */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessDashboard;
